import {assertUnreachable} from '@wandb/weave/common/util/types';
import {create} from 'zustand';

import {apolloClient} from '../../../../apolloClient';
import {
  ArtifactCollectionNameFromIdDocument,
  EventTriggeringConditionType,
  TriggerScopeType,
  UpsertModelDocument,
} from '../../../../generated/graphql';
import {OLD_MODEL_REGISTRY_PROJECT_NAME} from '../../../../util/artifacts';
import {propagateErrorsContext} from '../../../../util/errors';
import {FilterKey, ValueFilter} from '../../../../util/filterTypes';
import {
  createArtifactFilter,
  getValueFromArtifactFilterString,
} from '../filterUtils';
import {EventTypeToMetadata} from '../TriggerEventTypes';
import {
  Trigger,
  TriggerDetails,
  TriggeredActionType,
} from '../TriggersTable/utilTypes';
import {ProjectFromNameQuery} from './ProjectFromName.query';
import {
  isValidRunMetricChangeConfig,
  RunMetricChangeActions,
  RunMetricChangeConfig,
} from './RunMetricChangeSection';
import {
  isValidRunMetricThresholdConfig,
  RunMetricThresholdActions,
  RunMetricThresholdConfig,
} from './RunMetricThresholdSection';
import {
  isValidRunMetricZscoreConfig,
  RunMetricZscoreActions,
  RunMetricZscoreConfig,
} from './RunMetricZscoreSection';
import {
  isValidRunStateConfig,
  RunStateActions,
  RunStateConfig,
} from './RunStateSection';
import {EntryPoint, TriggerFeatureFlags} from './types';

/*

This file contains business logic for the event page of the create trigger drawer.

# Why does this use zustand for state management?
This is a complex multi-layer component, and having all the state/props passed
around makes it hard to tell what data is used where.
Also, moving state management up in the component tree meant that the top components
were getting too complex, and there was logic affecting the various pieces of state
scattered in different parts of the component tree, making it hard to
understand what was going on.

# Why use 2 separate stores for Events and Actions?
It was previously confusing as to what some shared fields are used for - eg "entityName" was
used for both event and action config, but it actually should be separate. I believe
the previous shared config may also have contributed to some minor bugs/odd behavior.
I'm new to using zustand, so as we learn more, it would be good to revisit this decision.

# What future refactorings could we do to simplify this component?
1. Move the next button disable/enabling logic into the the pages - the page is what decides if you can go next, so
   it makes sense to maintain that logic there.
2. Move the create automation logic into the final summary page ( or even better, the zustand store)
*/

export type TriggerRootFilter = {
  op: 'OR';
  filters: [
    {
      op: 'AND';
      filters: Array<ValueFilter<FilterKey>>;
    },
  ];
};

// we use a function here so that you can't modify a shared "constant" version by mistake
export const getEmptyTriggerFilters = (): TriggerRootFilter => {
  return {
    op: 'OR',
    filters: [
      {
        op: 'AND',
        filters: [],
      },
    ],
  };
};

// This is exported so that we can mock it in tests
export const eventStoreDeps = {
  apolloQuery: apolloClient.query,
};

// different entry points have different required fields,
// so differentiating types to make it easier to work with.
// IF YOU CHANGE THESE TYPES, MAKE SURE TO UPDATE THE TESTS
// eg - if you change it so that a field can now be undefined, make sure
// that there is a test covering that case.
//
// I looked into removing the artifactCollectionID/artifactCollectionName fields
// from the types, but it made it harder to write generic code, so I left them in.
export type CreateTriggerDrawerInit = (
  | {
      isUpdating: false;
      trigger: undefined;
    }
  | {
      isUpdating: true;
      trigger: Trigger;
    }
) &
  BaseTriggerDrawerInit;

export type BaseTriggerDrawerInit =
  | {
      entryPoint: EntryPoint.PROJECT;
      eventEntityName: string;
      eventProjectID: string;
      eventProjectName: string;
      artifactCollectionID: undefined;
      artifactCollectionName: undefined;
      existingAutomations: TriggerDetails[];
      actionEntityName: string;
      suggestedMetricName: undefined;
      defaultEvent: undefined;
      defaultAction: undefined;
      panelLink: undefined;
    }
  | {
      entryPoint: EntryPoint.REGISTRY;
      eventEntityName: string;
      eventProjectID: string;
      eventProjectName: string;
      artifactCollectionID: undefined;
      artifactCollectionName: undefined;
      existingAutomations: TriggerDetails[];
      // we don't have a default entity for actions, since actions can't run in the registry entity
      actionEntityName: undefined;
      suggestedMetricName: undefined;
      defaultEvent: undefined;
      defaultAction: undefined;
      panelLink: undefined;
    }
  | {
      entryPoint: EntryPoint.PORTFOLIO;
      eventEntityName: string;
      eventProjectID: string;
      eventProjectName: string;
      artifactCollectionID: string;
      artifactCollectionName: string;
      existingAutomations: TriggerDetails[];
      // we don't have a default entity for actions, since actions can't run in the registry entity
      actionEntityName: undefined;
      suggestedMetricName: undefined;
      defaultEvent: undefined;
      defaultAction: undefined;
      panelLink: undefined;
    }
  | {
      entryPoint: EntryPoint.TEAM_MODEL_REGISTRY;
      // entityName will be undefined when the user has picked the "all teams"
      // option in the legacy model registry's team picker dropdown
      eventEntityName: string | undefined;
      // In the case where entityName is undefined, we don't know the projectID
      // we also don't look up the projectID in the calling code, so we avoid
      // writing that code and just always assume we will have to query for it
      // and pass it in undefined.
      eventProjectID: undefined;
      eventProjectName: typeof OLD_MODEL_REGISTRY_PROJECT_NAME;
      artifactCollectionID: undefined;
      artifactCollectionName: undefined;
      existingAutomations: TriggerDetails[];
      // we don't have a default entity for actions, since actions can't run in the registry entity
      actionEntityName: undefined;
      suggestedMetricName: undefined;
      defaultEvent: undefined;
      defaultAction: undefined;
      panelLink: undefined;
    }
  | {
      entryPoint: EntryPoint.WORKSPACE_PANEL;
      eventEntityName: string;
      eventProjectID: string;
      eventProjectName: string;
      artifactCollectionID: undefined;
      artifactCollectionName: undefined;
      existingAutomations: [];
      suggestedMetricName: string;
      actionEntityName: string;
      defaultEvent: EventTriggeringConditionType;
      defaultAction: TriggeredActionType;
      panelLink: string | undefined;
    };

export const getProjectTriggerDrawerInit = (
  entityName: string,
  projectID: string,
  projectName: string,
  existingAutomations: TriggerDetails[]
): BaseTriggerDrawerInit => {
  return {
    entryPoint: EntryPoint.PROJECT,
    eventEntityName: entityName,
    eventProjectID: projectID,
    eventProjectName: projectName,
    artifactCollectionID: undefined,
    artifactCollectionName: undefined,
    existingAutomations,
    actionEntityName: entityName,
    suggestedMetricName: undefined,
    defaultEvent: undefined,
    defaultAction: undefined,
    panelLink: undefined,
  };
};

export const getTriggerDrawerInitForCollection = (
  projectID: string,
  projectName: string,
  entityName: string,
  artifactCollectionID: string,
  artifactCollectionName: string,
  existingAutomations: TriggerDetails[]
): BaseTriggerDrawerInit => {
  return {
    eventProjectID: projectID,
    eventProjectName: projectName,
    eventEntityName: entityName,
    artifactCollectionID,
    entryPoint: EntryPoint.PORTFOLIO,
    existingAutomations,
    artifactCollectionName,
    actionEntityName: undefined,
    suggestedMetricName: undefined,
    defaultEvent: undefined,
    defaultAction: undefined,
    panelLink: undefined,
  };
};

export const getTeamModelRegistryTriggerDrawerInit = (
  entityName: string | undefined
): BaseTriggerDrawerInit => {
  return {
    entryPoint: EntryPoint.TEAM_MODEL_REGISTRY,
    eventEntityName: entityName,
    eventProjectID: undefined,
    eventProjectName: OLD_MODEL_REGISTRY_PROJECT_NAME,
    artifactCollectionID: undefined,
    artifactCollectionName: undefined,
    existingAutomations: [],
    actionEntityName: undefined,
    suggestedMetricName: undefined,
    defaultEvent: undefined,
    defaultAction: undefined,
    panelLink: undefined,
  };
};

export const getWorkspacePanelTriggerDrawerInit = (
  entityName: string,
  projectID: string,
  projectName: string,
  suggestedMetricName: string,
  defaultEvent: EventTriggeringConditionType,
  defaultAction: TriggeredActionType,
  panelLink: string | undefined
): BaseTriggerDrawerInit => {
  return {
    entryPoint: EntryPoint.WORKSPACE_PANEL,
    eventEntityName: entityName,
    eventProjectID: projectID,
    eventProjectName: projectName,
    artifactCollectionID: undefined,
    artifactCollectionName: undefined,
    existingAutomations: [],
    actionEntityName: entityName,
    suggestedMetricName,
    defaultEvent,
    defaultAction,
    panelLink,
  };
};

export const getCtdWrapperForBaseInit = (
  init: BaseTriggerDrawerInit
): CreateTriggerDrawerInit => {
  return {
    ...init,
    isUpdating: false,
    trigger: undefined,
  };
};

export type EventConfig = {
  // This entityName for events is really meant to be (in current code) a proxy
  // of the entity the user is looking at in the UI when they launch
  // this trigger creation flow. It was a UI design decision that we don't want
  // users to change the event's scope as they configure the automation.
  // This will be undefined when the user has picked the "all teams"
  // option in the legacy model registry's team picker dropdown.
  entityName: string | undefined;
  // in many cases projectName/projectID will be defined, but
  // when it's not, it is queried for during the asyncInit call and/or when
  // the user changes the entityName
  projectName: string | undefined;
  projectID: string | undefined;

  filters: TriggerRootFilter;
  scopeType: TriggerScopeType | undefined;
  scopeID: string | undefined;
  // when the scopeType is ArtifactCollection, this will be the name of the collection
  scopeArtifactCollectionName: string | undefined;

  initProps: {
    artifactCollectionID?: string;
    // right now, artifactCollectionName is a bit overloaded - when
    // the user passes in a artifactCollectionID, it is set to the name of the collection
    // but in contexts when the user picks a collection, it will be set to the name
    // of the artifact collection pointed at ScopeId.
    artifactCollectionName?: string;
    suggestedMetricName?: string;
    panelLink?: string;
    isUpdating: boolean;
    existingTrigger?: Trigger;
  };
  featureFlags: TriggerFeatureFlags;
  loading: boolean;

  entryPoint: EntryPoint | undefined;

  eventType: EventTriggeringConditionType | undefined;
  availableEventTypes: EventTriggeringConditionType[];
  // This flag lets us know if we should allow event entityname changes for the event
  allowEntityNameChange: boolean;
  existingAutomations: TriggerDetails[];

  // Future improvment: TriggerEventConfiguration sets this in a useEffect, but it'd be good to move
  // more of event configuration into CreateTriggerLogic, so we can calculate this.
  isAddAliasConfigurationOk: boolean;
  isAddTagConfigurationOk: boolean;

  // calculated fields
  getShowSelectTeam: () => boolean;
  getAvailableConditionTypes: () => EventTriggeringConditionType[];
  getAllowArtifactSelection: () => boolean;
};

// keeping this state separately makes it easy to reset the state back to starting conditions
const initialEventConfig = {
  eventType: undefined,
  availableEventTypes: [],
  allowEntityNameChange: true,
  existingAutomations: [],
  initProps: {
    artifactCollectionID: undefined,
    artifactCollectionName: undefined,
    isUpdating: false,
    panelLink: undefined,
  },
  featureFlags: {
    metricChangeNotificationsEnabled: false,
    runMetricZscoreEnabled: false,
  },
  entityName: undefined,
  projectName: undefined,
  projectID: undefined,
  entryPoint: undefined,

  isAddAliasConfigurationOk: true, // default to true since we don't know if that's what the user will pick
  isAddTagConfigurationOk: true, // default to true since we don't know if that's what the user will pick
  filters: getEmptyTriggerFilters(),
  // TODO: consider making scopeType/scopeID calculated fields based on event type/etc
  scopeID: undefined,
  scopeType: undefined,
  scopeArtifactCollectionName: undefined,
  loading: false, // this gets set to true, then false in initAsync
};

export const useEventStore = create<EventConfig>()((set, get) => ({
  ...initialEventConfig,

  ///////////// calculated fields /////////////
  // why do we keep these on the store? I want to be available to the user
  // when they are looking at the autocomplate for the store - basically,
  // I don't want a user to use a field on the store, when there's a better
  // calculated field. This is experimental, so maybe it's a better idea to
  // put them on a separate object like EventStoreActions, or potentially to
  // make them into selectors so they can force re-renders only when they change.

  // Note that we could also allow users to select an automation when using
  // the registry entry point, but we want users to use the collection entry point
  // when they want to create a collection specific automation, so we discourage that.
  // We allow it for projects because there isn't an equivalent artifact level entry point.
  getAllowArtifactSelection: () => get().entryPoint === EntryPoint.PROJECT,
  // A check for just event entity name here would prevent the user from
  // changing the entity after they pick the first one.
  getShowSelectTeam: () => get().allowEntityNameChange,
  getAvailableConditionTypes: () => {
    const flags = get().featureFlags;
    let types = get().availableEventTypes;

    // Filter out metric change notifications if flag is disabled
    if (!flags.metricChangeNotificationsEnabled) {
      types = types.filter(
        eventType =>
          eventType !== EventTriggeringConditionType.RunMetricChange &&
          eventType !== EventTriggeringConditionType.RunMetric &&
          eventType !== EventTriggeringConditionType.RunState
      );
    }

    // Filter out zscore if flag is disabled
    if (!flags.runMetricZscoreEnabled) {
      types = types.filter(
        eventType => eventType !== EventTriggeringConditionType.RunMetricZscore
      );
    }

    return types;
  },
}));

// This used to be inside of the store, but I moved it out because
// we need this to be recalculated anytime the eventConfig changes,
// but if you just read the function from the store in your component's
// selector, the function doesn't when the eventconfig changes. So
// then your component doesn't get the latest state.
//
// This function forces you to pass in the config, so it forces the
// calling component to select the eventConfig, which means this will
// get recalculated anytime the eventConfig changes.
export const isEventValid = (
  eventConfig: EventConfig,
  runMetricConfig: RunMetricThresholdConfig,
  runMetricChangeConfig: RunMetricChangeConfig,
  runStateConfig: RunStateConfig,
  runMetricZscoreConfig: RunMetricZscoreConfig
) => {
  switch (eventConfig.eventType) {
    case undefined:
      return false;
    case EventTriggeringConditionType.AddArtifactAlias:
      return eventConfig.isAddAliasConfigurationOk;
    case EventTriggeringConditionType.AddArtifactTag:
      return eventConfig.isAddTagConfigurationOk;
    case EventTriggeringConditionType.CreateArtifact:
    case EventTriggeringConditionType.LinkModel:
    case EventTriggeringConditionType.UpdateArtifactAlias:
      return eventConfig.scopeType != null && eventConfig.scopeID != null;
    case EventTriggeringConditionType.RunMetric:
      return isValidRunMetricThresholdConfig(runMetricConfig);
    case EventTriggeringConditionType.RunMetricChange:
      return isValidRunMetricChangeConfig(runMetricChangeConfig);
    case EventTriggeringConditionType.RunState:
      return isValidRunStateConfig(runStateConfig);
    case EventTriggeringConditionType.RunMetricZscore:
      return isValidRunMetricZscoreConfig(runMetricZscoreConfig);
    default:
      assertUnreachable(eventConfig.eventType);
  }
};

// note that we don't export these since they should only be used
// in the actions/tests.
const eventSet = useEventStore.setState;
const eventGet = useEventStore.getState;

// These are actions intended by be used by components
// No other functions should be calling setState() on the store.
// We decided to not put these into the store object, because it's a bit silly
// to have to pull the actions out using a selector, and there doesn't
// appear to be any big downside to having them outside. It also makes testing
// a bit easier.
export const EventStoreActions = {
  // constructor-ish function. Intended to be called once each time
  // the drawer is opened
  initAsync: async (
    asyncInitProps: CreateTriggerDrawerInit,
    // we pass in flags so that it's easier to test (fewer queries to mock, just pass flag value in),
    //  and since the flag can be loaded in the parent component before this is initialized
    featureFlags: TriggerFeatureFlags
  ) => {
    // TODO: it's annoying to have to do this, but there seem to be some cases
    // where the projectID is set to an empty string in the init props, and
    // we need to take some time to go find those and fix them before we can
    // remove this code.
    let callerProjectID = asyncInitProps.eventProjectID;
    if (callerProjectID === '') {
      callerProjectID = undefined;
    }
    let callerEntityName = asyncInitProps.eventEntityName;
    if (callerEntityName === '') {
      callerEntityName = undefined;
    }
    eventSet({
      ...initialEventConfig, // First, reset the state back to starting conditions
      initProps: {
        artifactCollectionID: asyncInitProps.artifactCollectionID,
        artifactCollectionName: asyncInitProps.artifactCollectionName,
        suggestedMetricName: asyncInitProps.suggestedMetricName,
        isUpdating: asyncInitProps.isUpdating,
        existingTrigger: asyncInitProps.trigger,
        panelLink: asyncInitProps.panelLink,
      },
      loading: true,
      allowEntityNameChange: asyncInitProps.eventEntityName == null,
      availableEventTypes: entryPointToEventOptions[asyncInitProps.entryPoint],
      existingAutomations: asyncInitProps.existingAutomations,
      featureFlags,
      entryPoint: asyncInitProps.entryPoint,
      projectName: asyncInitProps.eventProjectName,
      entityName: callerEntityName,
      projectID: callerProjectID,
    });

    if (asyncInitProps.isUpdating) {
      await EventStoreActions.initForEditTrigger(asyncInitProps);
    } else {
      await EventStoreActions.initForCreateTrigger(asyncInitProps);
    }
    await EventStoreActions.private.finishInitAfterProjectDataLoadedAsync(
      callerProjectID,
      callerEntityName
    );
  },

  initForEditTrigger: async (asyncInitProps: CreateTriggerDrawerInit) => {
    if (!asyncInitProps.isUpdating) {
      throw new Error('initForEditTrigger called with isUpdating: false');
    }
    if (!asyncInitProps.trigger?.id) {
      throw new Error('initForEditTrigger called with invalid triggerId');
    }
    const artifactCollectionNameRes =
      asyncInitProps.trigger.triggerScope.id === '' ||
      asyncInitProps.trigger.triggerScope.scope === TriggerScopeType.Project
        ? undefined
        : await eventStoreDeps.apolloQuery({
            query: ArtifactCollectionNameFromIdDocument,
            variables: {
              artifactCollectionID: asyncInitProps.trigger.triggerScope.id,
            },
          });
    const trigger = asyncInitProps.trigger;
    const filters = getEmptyTriggerFilters();

    const alias = getValueFromArtifactFilterString(
      trigger.triggeringCondition.filter,
      'alias'
    );
    if (alias.length > 0) {
      filters.filters[0].filters.push(createArtifactFilter('alias', alias));
    }
    const tag = getValueFromArtifactFilterString(
      trigger.triggeringCondition.filter,
      'tag'
    );
    if (tag.length > 0) {
      filters.filters[0].filters.push(createArtifactFilter('tag', tag));
    }
    switch (trigger.triggeringCondition.eventType) {
      case EventTriggeringConditionType.RunMetric:
        RunMetricThresholdActions.initFromTrigger(trigger);
        break;
      case EventTriggeringConditionType.RunMetricChange:
        RunMetricChangeActions.initFromTrigger(trigger);
        break;
      case EventTriggeringConditionType.RunState:
        RunStateActions.initFromTrigger(trigger);
        break;
      case EventTriggeringConditionType.RunMetricZscore:
        RunMetricZscoreActions.initFromTrigger(trigger);
        break;
      default:
        break;
    }
    eventSet({
      eventType: trigger.triggeringCondition.eventType,

      isAddAliasConfigurationOk: true, // if it was valid enough to create trigger, it should still be valid :)
      isAddTagConfigurationOk: true, // if it was valid enough to create trigger, it should still be valid :)
      filters: filters,
      scopeID: trigger.triggerScope.id,
      scopeType: trigger.triggerScope.scope,
      scopeArtifactCollectionName:
        artifactCollectionNameRes?.data?.artifactCollection?.name,
      loading: false,
    });
  },

  initForCreateTrigger: async (asyncInitProps: CreateTriggerDrawerInit) => {
    const scopeSettings =
      asyncInitProps.artifactCollectionID == null
        ? {
            scopeType: undefined,
            scopeID: undefined,
            scopeArtifactCollectionName: undefined,
          }
        : {
            scopeType: TriggerScopeType.ArtifactCollection,
            scopeID: asyncInitProps.artifactCollectionID,
            scopeArtifactCollectionName: asyncInitProps.artifactCollectionName,
          };

    eventSet({
      ...scopeSettings,
      loading: false,
    });
    if (asyncInitProps.defaultEvent != null) {
      await EventStoreActions.setEventType(asyncInitProps.defaultEvent);
    }
  },

  setEntityNameAsync: async (entityName: string) => {
    if (!eventGet().allowEntityNameChange) {
      throw new Error(
        "can't setEntityName when entityName was passed in during asyncInit"
      );
    }
    eventSet({entityName});
    await EventStoreActions.private.finishInitAfterProjectDataLoadedAsync(
      eventGet().projectID,
      entityName
    );
  },

  setEventType: (eventType: EventTriggeringConditionType | undefined) => {
    // reset scopetype and related settings
    eventSet({eventType: eventType});
    // need to run initAsync for stores that need it
    switch (eventType) {
      case EventTriggeringConditionType.RunMetric:
        // intentionally not await'ing so this does not block this function
        RunMetricThresholdActions.initAsync(
          eventGet().initProps.suggestedMetricName,
          eventGet().initProps.panelLink
        );
        break;
      case EventTriggeringConditionType.RunMetricChange:
        // intentionally not await'ing so this does not block this function
        RunMetricChangeActions.initAsync(
          eventGet().initProps.suggestedMetricName,
          eventGet().initProps.panelLink
        );
        break;
      case EventTriggeringConditionType.RunState:
        RunStateActions.init();
        break;
      case EventTriggeringConditionType.CreateArtifact:
      case EventTriggeringConditionType.LinkModel:
      case EventTriggeringConditionType.AddArtifactAlias:
      case EventTriggeringConditionType.UpdateArtifactAlias:
      case EventTriggeringConditionType.AddArtifactTag:
      case undefined:
        // these do not need initialization when the event type changes
        break;
      case EventTriggeringConditionType.RunMetricZscore:
        // intentionally not await'ing so this does not block this function
        RunMetricZscoreActions.initAsync(
          eventGet().initProps.suggestedMetricName,
          eventGet().initProps.panelLink
        );
        break;
      default:
        assertUnreachable(eventType);
    }
    if (eventGet().initProps.artifactCollectionID != null) {
      eventSet({
        scopeType: TriggerScopeType.ArtifactCollection,
        scopeID: eventGet().initProps.artifactCollectionID,
        filters: getEmptyTriggerFilters(),
        scopeArtifactCollectionName:
          eventGet().initProps.artifactCollectionName,
      });
    } else {
      eventSet({
        scopeType: eventType
          ? EventTypeToMetadata[eventType].defaultScopeType
          : undefined,
        scopeID: undefined,
        filters: getEmptyTriggerFilters(),
        scopeArtifactCollectionName: undefined,
      });
    }
  },

  ///////////// simple setters /////////////
  // Making a section for these simply to convey that they don't do anything besides
  // call setState. I am keeping these separate (vs having one update() function that
  // works similar to set()) because:
  // 1. I'm finding that I frequently want to add
  //    logic that runs when use change a field, and if you have to convert it from
  //    an `update({myField: newVal})` to `setMyField(newVal)` it is annoying to
  //    modify all the callsites.
  // 2. It's very easy to add these methods here.
  // 3. It makes it easy to have readonly fields on the store. (you just don't
  //    define a setter)
  setProjectId: (projectID: string) => eventSet({projectID}),
  setIsAddAliasConfigurationOk: (isAddAliasConfigurationOk: boolean) =>
    eventSet({isAddAliasConfigurationOk}),
  setIsAddTagConfigurationOk: (isAddTagConfigurationOk: boolean) =>
    eventSet({isAddTagConfigurationOk}),
  setFilters: (filters: TriggerRootFilter) => {
    eventSet({filters});
  },
  setScopeType: (scopeType: TriggerScopeType | undefined) => {
    eventSet({scopeType});
  },
  setScopeID: (scopeID: string | undefined) => {
    eventSet({scopeID});
  },
  setArtifactCollectionName: (
    scopeArtifactCollectionName: string | undefined
  ) => {
    eventSet({scopeArtifactCollectionName});
  },

  ///////////// private actions /////////////
  // private actions are only intended to be called by functions inside of EventStoreActions
  private: {
    // keeping this outside of the store since it is often called immediately
    // after updating something in the store, and we want to make it very obvious
    // that you should not be reading from get() in this function.
    finishInitAfterProjectDataLoadedAsync: async (
      projectID: string | undefined,
      entityName: string | undefined
    ) => {
      // need entityname for the query below
      if (entityName == null) {
        return;
      }
      // the code below is only need if the caller didn't provide a projectID
      if (projectID != null) {
        return;
      }
      // This query for projects supports the case where the event's project id is not provided (which occurrs
      // in the TEAM_MODEL_REGISTRY case)
      const modelRegistryProjectIdFromQuery = await eventStoreDeps.apolloQuery({
        query: ProjectFromNameQuery,
        variables: {
          entityName,
          projectName: OLD_MODEL_REGISTRY_PROJECT_NAME,
        },
        fetchPolicy: 'cache-first',
      });
      const modelRegistryProjectId: string | undefined =
        modelRegistryProjectIdFromQuery.data?.project?.id;
      if (modelRegistryProjectId == null) {
        // Since the model registry project has not been created yet, we need to
        // create it.
        // The upsert model mutation is the correct way to create the project in this case,
        // despite the confusing legacy name.
        await apolloClient.mutate({
          context: propagateErrorsContext(),
          mutation: UpsertModelDocument,
          variables: {
            entityName,
            name: OLD_MODEL_REGISTRY_PROJECT_NAME,
          },
        });
      }

      eventSet({projectID: modelRegistryProjectId});
    },
  },
};

// Note that UpdateArtifactAlias and RunMetric are not currently supported as events
// so we don't include them in this list.
const entryPointToEventOptions: Record<
  EntryPoint,
  EventTriggeringConditionType[]
> = {
  [EntryPoint.PROJECT]: [
    EventTriggeringConditionType.CreateArtifact,
    EventTriggeringConditionType.AddArtifactAlias,
    EventTriggeringConditionType.AddArtifactTag,
    EventTriggeringConditionType.RunMetric,
    EventTriggeringConditionType.RunMetricChange,
    EventTriggeringConditionType.RunState,
    EventTriggeringConditionType.RunMetricZscore,
  ],
  [EntryPoint.REGISTRY]: [
    EventTriggeringConditionType.LinkModel,
    EventTriggeringConditionType.AddArtifactAlias,
    // AddArtifactTag is omitted for now, since the backend doesn't currently know when a tag is added
    // to a linked version in a registry collection vs to the source artifact version.
    // As far the automation is concerned, a tag is always added to the source artifact version.
    //
    // Revisit if we allow createArtifactTagAssignment() to recognize the registry/collection context
    // when a user is adding a tag **while** viewing registry/registry collection.
  ],
  [EntryPoint.PORTFOLIO]: [
    EventTriggeringConditionType.LinkModel,
    EventTriggeringConditionType.AddArtifactAlias,
  ],
  [EntryPoint.TEAM_MODEL_REGISTRY]: [
    EventTriggeringConditionType.LinkModel,
    EventTriggeringConditionType.AddArtifactAlias,
    // theoretically UpdateArtifact should work, but it's currently disabled in all cases
    // as an event, so we're not going to use it for now
    // EventTriggeringConditionType.UpdateArtifactAlias
  ],
  [EntryPoint.WORKSPACE_PANEL]: [
    EventTriggeringConditionType.RunMetric,
    EventTriggeringConditionType.RunMetricChange,
    EventTriggeringConditionType.RunState,
    EventTriggeringConditionType.RunMetricZscore,
  ],
};

export const getTemplateStrings = (
  eventType: EventTriggeringConditionType | undefined
) => {
  // It will be undefined when configuration is invalid, and we silently swallow that since
  // other parts of the code are responsible for making sure we never render components
  // that are taking this as input when config is invalid.
  if (eventType == null) {
    return [];
  }
  return EventTypeToMetadata[eventType].templateStrings;
};

export const isAliasOrTagEventConfiguredOk = (
  filterIsValid: boolean,
  allowCollectionSelection: boolean,
  isAddArtifactEnabled: boolean,
  triggerScopeID: string | undefined
) => {
  // You must always fill in an alias
  if (!filterIsValid) {
    return false;
  }
  // if we don't allow collection selection, then there's nothing else that
  // could be misconfigured
  if (!allowCollectionSelection) {
    return true;
  }

  // if they want to add an artifact filter, they have to select an artifact
  if (!isAddArtifactEnabled) {
    return true;
  }
  return triggerScopeID !== '' && triggerScopeID != null;
};
